import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Loader2, AlertTriangle, Ban, CalendarX } from "lucide-react";
import { Dentist } from "@/services/dentist-service";
import { DentalSpecialty, fetchSpecialtiesByClinic } from "@/services/dental-specialty-service";
import { SlotPreviewItem, previewReassignSlots, bulkReassignSlots, bulkBlockSlots } from "@/services/shift-reassign-service";

const TIME_SLOTS = [
  "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00", "17:30",
];

interface ShiftReassignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  dentists: Dentist[];
  initialDate: string; // YYYY-MM-DD
  onSuccess: () => void;
}

type Step = "form" | "preview" | "done";
type DestinationType = "dentist" | "blocked" | "blocked_external";

export function ShiftReassignDialog({
  open,
  onOpenChange,
  clinicId,
  dentists,
  initialDate,
  onSuccess,
}: ShiftReassignDialogProps) {
  const [step, setStep] = useState<Step>("form");
  const [loading, setLoading] = useState(false);

  // Campos do formulário
  const [date, setDate] = useState(initialDate);
  const [dentistFromId, setDentistFromId] = useState("");
  const [timeFrom, setTimeFrom] = useState("08:00");
  const [timeTo, setTimeTo] = useState("12:00");
  const [destinationType, setDestinationType] = useState<DestinationType>("dentist");
  const [dentistToId, setDentistToId] = useState("");
  const [specialtyToId, setSpecialtyToId] = useState("keep");
  const [reason, setReason] = useState("");

  // Dados auxiliares
  const [specialties, setSpecialties] = useState<DentalSpecialty[]>([]);
  const [previewSlots, setPreviewSlots] = useState<SlotPreviewItem[]>([]);
  const [previewStats, setPreviewStats] = useState({ total: 0, with_patient: 0, without_patient: 0 });

  useEffect(() => {
    if (open) {
      setDate(initialDate);
      setStep("form");
      setDentistFromId("");
      setDestinationType("dentist");
      setDentistToId("");
      setSpecialtyToId("keep");
      setReason("");
      setPreviewSlots([]);
      setPreviewStats({ total: 0, with_patient: 0, without_patient: 0 });
    }
  }, [open, initialDate]);

  useEffect(() => {
    if (clinicId) {
      fetchSpecialtiesByClinic(clinicId).then(setSpecialties).catch(() => {});
    }
  }, [clinicId]);

  const timeToMinutes = (t: string) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };

  const handlePreview = async () => {
    if (!dentistFromId || !date) return;
    if (destinationType === "dentist" && !dentistToId) return;
    setLoading(true);
    try {
      const result = await previewReassignSlots(
        clinicId, date, dentistFromId,
        `${timeFrom}:00`, `${timeTo}:00`,
      );
      setPreviewSlots(result.slots);
      setPreviewStats({ total: result.total, with_patient: result.with_patient, without_patient: result.without_patient });
      setStep("preview");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao buscar prévia");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      if (destinationType === "dentist") {
        await bulkReassignSlots({
          clinic_id: clinicId,
          slot_date: date,
          dentist_from_id: dentistFromId,
          time_from: `${timeFrom}:00`,
          time_to: `${timeTo}:00`,
          dentist_to_id: dentistToId,
          specialty_to_id: specialtyToId !== "keep" ? specialtyToId : undefined,
          reason: reason || undefined,
        });
      } else {
        await bulkBlockSlots({
          clinic_id: clinicId,
          slot_date: date,
          dentist_from_id: dentistFromId,
          time_from: `${timeFrom}:00`,
          time_to: `${timeTo}:00`,
          block_type: destinationType === "blocked" ? "BLOCKED" : "BLOCKED_EXTERNAL",
          reason: reason || undefined,
        });
      }
      setStep("done");
      onSuccess();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao processar turno");
    } finally {
      setLoading(false);
    }
  };

  const dentistFromName = dentists.find(d => d.id === dentistFromId)?.name ?? "";
  const dentistToName = dentists.find(d => d.id === dentistToId)?.name ?? "";
  const withPatient = previewStats.with_patient;
  const empty = previewStats.without_patient;

  const destinationLabel =
    destinationType === "dentist" ? dentistToName :
    destinationType === "blocked" ? "Bloqueado" :
    "Bloqueado Externo";

  const canPreview =
    !!dentistFromId && !!date &&
    (destinationType === "dentist" ? !!dentistToId && dentistFromId !== dentistToId : true);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-primary" />
            Substituição de Turno
          </DialogTitle>
        </DialogHeader>

        {/* STEP: FORM */}
        {step === "form" && (
          <div className="space-y-4">
            {/* Data */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="filter-label">Data</Label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="filter-input w-full rounded-md border border-input px-2"
                />
              </div>
            </div>

            {/* Origem */}
            <div className="rounded-lg border border-border p-3 space-y-3 bg-muted/30">
              <p className="text-[10px] font-bold uppercase text-muted-foreground tracking-wider">Origem</p>
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-2 space-y-1">
                  <Label className="filter-label">Dentista atual</Label>
                  <Select value={dentistFromId} onValueChange={setDentistFromId}>
                    <SelectTrigger className="filter-input">
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {dentists.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="filter-label">Horário inicial</Label>
                  <Select value={timeFrom} onValueChange={setTimeFrom}>
                    <SelectTrigger className="filter-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="filter-label">Horário final</Label>
                  <Select value={timeTo} onValueChange={setTimeTo}>
                    <SelectTrigger className="filter-input">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.filter(t => timeToMinutes(t) > timeToMinutes(timeFrom)).map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Destino */}
            <div className="rounded-lg border border-primary/30 p-3 space-y-3 bg-primary/5">
              <p className="text-[10px] font-bold uppercase text-primary tracking-wider">Destino</p>

              {/* Tipo de destino */}
              <div className="flex gap-2">
                {([
                  { value: "dentist",         label: "Substituir dentista", icon: <ArrowRight className="h-3 w-3" /> },
                  { value: "blocked",         label: "Bloquear",            icon: <Ban className="h-3 w-3" /> },
                  { value: "blocked_external",label: "Bloquear externo",    icon: <CalendarX className="h-3 w-3" /> },
                ] as { value: DestinationType; label: string; icon: React.ReactNode }[]).map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setDestinationType(opt.value)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-semibold border transition-colors ${
                      destinationType === opt.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:border-primary/50"
                    }`}
                  >
                    {opt.icon}{opt.label}
                  </button>
                ))}
              </div>

              {/* Campos: só para substituição */}
              {destinationType === "dentist" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="filter-label">Dentista substituto</Label>
                    <Select value={dentistToId} onValueChange={setDentistToId}>
                      <SelectTrigger className="filter-input">
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        {dentists.filter(d => d.id !== dentistFromId).map(d => (
                          <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="filter-label">Especialidade (opcional)</Label>
                    <Select value={specialtyToId} onValueChange={setSpecialtyToId}>
                      <SelectTrigger className="filter-input">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="keep">Manter original</SelectItem>
                        {specialties.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Aviso para modos de bloqueio */}
              {destinationType !== "dentist" && (
                <div className="flex items-center gap-2 p-2 rounded bg-orange-50 border border-orange-200 text-xs text-orange-800">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    {destinationType === "blocked"
                      ? "Os slots serão bloqueados — nenhum agendamento será permitido."
                      : "Os slots serão marcados como bloqueados para agendamento externo."}
                  </span>
                </div>
              )}
            </div>

            {/* Motivo */}
            <div className="space-y-1">
              <Label className="filter-label">Motivo / Observação</Label>
              <Textarea
                placeholder="Ex: Dentista ausente por atestado médico"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="text-xs resize-none h-16"
              />
            </div>
          </div>
        )}

        {/* STEP: PREVIEW */}
        {step === "preview" && (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border text-xs">
              <span className="font-semibold">{dentistFromName}</span>
              <ArrowRight className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="font-semibold text-primary">{destinationLabel}</span>
              <span className="ml-auto text-muted-foreground">{date} · {timeFrom} – {timeTo}</span>
            </div>

            {previewSlots.length === 0 ? (
              <div className="flex items-center gap-2 p-4 rounded-lg border border-dashed text-muted-foreground">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs">Nenhum slot encontrado para esse intervalo.</span>
              </div>
            ) : (
              <>
                <div className="flex gap-2 text-xs">
                  <Badge variant="default" className="text-xs py-0">{previewStats.total} slots total</Badge>
                  <Badge variant="secondary" className="text-xs py-0">{withPatient} com paciente</Badge>
                  <Badge variant="outline" className="text-xs py-0">{empty} vazios</Badge>
                </div>

                <div className="rounded-lg border border-border overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 sticky top-0">
                      <tr>
                        <th className="th text-left">Horário</th>
                        <th className="th text-left" colSpan={2}>Paciente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewSlots.map((slot) => (
                        <tr key={slot.slot_id} className="border-t border-border hover:bg-muted/30">
                          <td className="td font-mono">{slot.slot_time.slice(0, 5)}</td>
                          <td className="td" colSpan={2}>
                            {slot.has_patient
                              ? <span className="font-medium uppercase">{slot.patient_name}</span>
                              : <span className="text-muted-foreground italic">— vazio —</span>
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {withPatient > 0 && (
                  <div className="flex items-start gap-2 p-2 rounded bg-yellow-50 border border-yellow-200 text-xs text-yellow-800">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>
                      {destinationType === "dentist"
                        ? `${withPatient} agendamento(s) com paciente serão transferidos para ${dentistToName}.`
                        : `${withPatient} agendamento(s) com paciente serão afetados pelo bloqueio.`}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* STEP: DONE */}
        {step === "done" && (
          <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <ArrowRight className="h-5 w-5 text-primary" />
            </div>
            <p className="text-sm font-semibold">
              {destinationType === "dentist" ? "Substituição realizada com sucesso!" : "Bloqueio realizado com sucesso!"}
            </p>
            <p className="text-xs text-muted-foreground">
              {destinationType === "dentist"
                ? <>Os slots foram transferidos de <strong>{dentistFromName}</strong> para <strong>{dentistToName}</strong>.</>
                : <>Os slots de <strong>{dentistFromName}</strong> foram marcados como <strong>{destinationLabel}</strong>.</>}
            </p>
          </div>
        )}

        <DialogFooter>
          {step === "form" && (
            <>
              <Button variant="outline" size="sm" className="btn-header" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button size="sm" className="btn-header" onClick={handlePreview} disabled={!canPreview || loading}>
                {loading && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Ver afetados
              </Button>
            </>
          )}
          {step === "preview" && (
            <>
              <Button variant="outline" size="sm" className="btn-header" onClick={() => setStep("form")}>
                Voltar
              </Button>
              <Button size="sm" className="btn-header" onClick={handleConfirm}
                disabled={loading || previewStats.total === 0}>
                {loading && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                {destinationType === "dentist" ? "Confirmar substituição" : "Confirmar bloqueio"}
              </Button>
            </>
          )}
          {step === "done" && (
            <Button size="sm" className="btn-header" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
